/**
 * Outline button that toggles a card between its original free text and an
 * on-device machine translation. Shows "Translate to {language}" on the original
 * and "Show original" while translated, with a spinner during translation.
 */
import { useTranslation } from "@refinedev/core";

import TranslateIcon from "@mui/icons-material/Translate";
import { Button, CircularProgress } from "@mui/material";

interface TranslateToggleButtonProps {
  /** True while the translated copy is shown. */
  isTranslated: boolean;
  /** True while a translation is in flight. */
  isLoading: boolean;
  /** Target language name in the current UI language (e.g. "French"). */
  targetLanguageLabel: string;
  /** Toggles between original and translated. */
  onClick: () => void;
  /** Test id for the rendered button. */
  "data-testid"?: string;
}

/**
 * Renders the translate/show-original toggle button for a card.
 *
 * @param props - The toggle state, target language label, and click handler.
 * @returns The button element.
 */
export const TranslateToggleButton = ({
  isTranslated,
  isLoading,
  targetLanguageLabel,
  onClick,
  "data-testid": dataTestId,
}: TranslateToggleButtonProps) => {
  const { translate } = useTranslation();

  const label = isTranslated
    ? translate("freeTextTranslation.showOriginal")
    : translate("freeTextTranslation.translateTo", { language: targetLanguageLabel });

  const startIcon = isLoading ? (
    <CircularProgress size={16} color="inherit" />
  ) : isTranslated ? undefined : (
    <TranslateIcon fontSize="small" />
  );

  return (
    <Button
      variant="outlinedRect"
      size="small"
      onClick={onClick}
      disabled={isLoading}
      startIcon={startIcon}
      data-testid={dataTestId}
    >
      {label}
    </Button>
  );
};
